import React, { useState } from 'react';
import { Icon, Modal } from 'Components';
import { useTranslation } from 'react-i18next';
import LMSButton from 'LearningManager/components/Button';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { pluralLabel } from 'LearningManager/helpers/role';
import useLearnerCount from 'LearningManager/hooks/useLearnerCount';
import useTenantScope from 'LearningManager/hooks/useTenantScope';
import { CourseAssignmentOption } from 'LearningManager/pages/CourseAssignments';
import styles from './CourseResetModal.module.scss';

interface CourseResetModalProps {
	visible: boolean;
	courseId: number;
	options: CourseAssignmentOption[];
	onCancel: () => void;
	onConfirm: (assignmentIds: number[]) => void;
}

const CourseResetModal: React.FC<CourseResetModalProps> = ({
	visible,
	courseId,
	options,
	onCancel,
	onConfirm
}) => {
	const [userCount, setUserCount] = useState(0);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const { globalTenantId } = useTenantScope();
	const { loading, getCount } = useLearnerCount(globalTenantId);
	const { t } = useTranslation();
	const hasSelection = selectedIds.length > 0;

	const selectAssignment = async (ids: number[]) => {
		setSelectedIds(ids);
		if(ids.length && courseId) {
			const { count } = await getCount('course', { assignment_source_ids: ids }, true, courseId);
			setUserCount(count);
		} else setUserCount(0);
	}

	const handleCancel = () => {
		setSelectedIds([]);
		onCancel();
	};

	const handleConfirm = () => {
		onConfirm(selectedIds);
		setSelectedIds([]);
	};

	return (
		<Modal
			visible={visible}
			title={t('learning.courseAssignments.courseResetModal.title')}
			onCancel={handleCancel}
			size="sm"
			destroyOnClose
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<LMSButton
						size="small"
						variant="borderless"
						type="default"
						onClick={handleCancel}
					>
						{t('learning.courseAssignments.courseResetModal.cancel')}
					</LMSButton>

					<LMSButton
						type="primary"
						size="small"
						disabled={!hasSelection || !userCount}
						onClick={handleConfirm}
					>
						{t('learning.courseAssignments.courseResetModal.reset')}
					</LMSButton>
				</div>
			}
		>
			<div className={styles.body}>
				<p className={styles.text}>
					{t(
						'learning.courseAssignments.courseResetModal.selectAssignments'
					)}
				</p>

				<div className={styles.field}>
					<div className={styles.field__label}>
						{t('learning.courseAssignments.courseResetModal.assignment')}
					</div>

					<MultiSelectDropdown
						options={options.map(({id, name}) => ({ id, label: name }))}
						placeholder={t(
							'learning.courseAssignments.courseResetModal.selectAssignment'
						)}
						defaultSelectedIds={[]}
						onChange={selectAssignment}
						allLabel={t(
							'learning.courseAssignments.courseResetModal.allAssignments'
						)}
						showSelectedLabels
						className={styles.dropdown}
					/>
				</div>

				{!hasSelection && (
					<div className={styles.summary}>
						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.courseProgress',
								{
									userCount:
										loading
											? ''
											: t(
												pluralLabel('learning.courseAssignments.courseResetModal.users', userCount),
												{ count: userCount }
											)
								}
							)}

							{loading && (
								<Icon
									type="loading"
									aria-label={t(
										'learning.courseAssignments.courseResetModal.countingUsers'
									)}
								/>
							)}
						</p>

						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.progressRemoved'
							)}
						</p>
						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.startAgain'
							)}
						</p>
						<p className={styles.text}>
							{t(
								'learning.courseAssignments.courseResetModal.cannotBeUndone'
							)}
						</p>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default CourseResetModal;